import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Recharts
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Context
import { useMaterialUIController } from "context";

// Services
import { getOverview, getTrends } from "services/dashboardService";

// Mock Data for New Sections
const mockActivity = [
  { time: "10:32 AM", text: "User uploaded Physics.pdf" },
  { time: "10:30 AM", text: "AI generated quiz" },
  { time: "10:25 AM", text: "New user registered" },
  { time: "09:14 AM", text: "Vector indexing completed" },
  { time: "08:55 AM", text: "User downloaded 3 documents" },
];

// ─── Minimal Stat Card ───────────────────────────────────────────────────────
function StatCard({ title, count, trend, trendLabel, darkMode }) {
  return (
    <div
      className={`rounded-xl p-6 flex flex-col justify-between h-full border transition-colors shadow-sm ${
        darkMode
          ? "bg-bnova-card border-bnova-border hover:border-white/10"
          : "bg-white border-slate-200/60 hover:border-slate-300"
      }`}
    >
      <span
        className={`text-[12px] font-semibold tracking-wider uppercase mb-4 ${
          darkMode ? "text-bnova-textSecondary" : "text-slate-500"
        }`}
      >
        {title}
      </span>
      <span
        className={`text-[38px] font-bold leading-none mb-3 tracking-tight ${
          darkMode ? "text-bnova-textPrimary" : "text-slate-800"
        }`}
      >
        {count}
      </span>
      <div
        className={`mt-auto text-[12px] flex items-center gap-1.5 ${
          darkMode ? "text-bnova-textSecondary" : "text-slate-500"
        }`}
      >
        <span className="text-violet-500 font-semibold">{trend}</span>
        <span>{trendLabel}</span>
      </div>
    </div>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  trend: PropTypes.string.isRequired,
  trendLabel: PropTypes.string.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

// ─── Minimal Line Chart Card ─────────────────────────────────────────────────
function MinimalChart({ title, data, color, darkMode }) {
  return (
    <div
      className={`rounded-xl p-6 h-[320px] flex flex-col border transition-colors ${
        darkMode
          ? "bg-bnova-card border-bnova-border hover:border-white/10"
          : "bg-white border-slate-200/60 hover:border-slate-300"
      }`}
    >
      <span
        className={`text-[14px] font-semibold mb-4 ${
          darkMode ? "text-bnova-textPrimary" : "text-slate-800"
        }`}
      >
        {title}
      </span>
      <div className="flex-1 w-full -ml-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <XAxis dataKey="name" hide />
            <YAxis hide domain={["dataMin - 10", "dataMax + 10"]} />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: darkMode ? "#111827" : "#ffffff",
                border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                borderRadius: "8px",
                color: darkMode ? "#F8FAFC" : "#0f172a",
              }}
              itemStyle={{ color: darkMode ? "#F8FAFC" : "#0f172a" }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: color }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

MinimalChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

function Dashboard() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const overviewData = await getOverview();
        setOverview(overviewData);
      } catch (err) {
        console.error("Dashboard data fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Format real data or use mock data for charts
  const userGrowthData = [
    { name: "Mon", value: 120 },
    { name: "Tue", value: 132 },
    { name: "Wed", value: 145 },
    { name: "Thu", value: 150 },
    { name: "Fri", value: 180 },
    { name: "Sat", value: 210 },
    { name: "Sun", value: 254 },
  ];

  const aiUsageData = [
    { name: "Mon", value: 4000 },
    { name: "Tue", value: 3000 },
    { name: "Wed", value: 5000 },
    { name: "Thu", value: 8000 },
    { name: "Fri", value: 6000 },
    { name: "Sat", value: 9000 },
    { name: "Sun", value: 12431 },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={4} px={2} className="max-w-[1400px] mx-auto">
        {/* Header Section */}
        <div className="mb-12">
          <h1
            className={`text-[32px] font-semibold tracking-tight mb-2 ${
              darkMode ? "text-[#F8FAFC]" : "text-slate-800"
            }`}
          >
            Welcome back, Admin
          </h1>
          <p className={`text-[15px] ${darkMode ? "text-[#94A3B8]" : "text-slate-500"}`}>
            {overview?.users?.active || 124} users active today
          </p>
        </div>

        {/* KPI Row */}
        <Grid container spacing={4} mb={6}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Total Users"
              count={overview?.users?.total || "1,254"}
              trend="+12%"
              trendLabel="this month"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Active Users"
              count={overview?.users?.active || "124"}
              trend="+5%"
              trendLabel="this week"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Library Books"
              count={overview?.content?.libraryBooks || "892"}
              trend="+24"
              trendLabel="new this week"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Search Queries"
              count={overview?.engagement?.queries || "45,231"}
              trend="+18%"
              trendLabel="vs last month"
              darkMode={darkMode}
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={4} mb={6}>
          <Grid item xs={12} md={6}>
            <MinimalChart
              title="User Growth"
              data={userGrowthData}
              color="#8B5CF6"
              darkMode={darkMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MinimalChart
              title="AI Usage (Requests)"
              data={aiUsageData}
              color="#10B981"
              darkMode={darkMode}
            />
          </Grid>
        </Grid>

        {/* Details Row */}
        <Grid container spacing={4} mb={6}>
          {/* Recent Activity */}
          <Grid item xs={12} md={4}>
            <div
              className={`rounded-xl p-6 h-full border transition-colors shadow-sm ${
                darkMode
                  ? "bg-bnova-card border-bnova-border hover:border-white/10"
                  : "bg-white border-slate-200/60 hover:border-slate-300"
              }`}
            >
              <span
                className={`text-[14px] font-semibold mb-4 block ${
                  darkMode ? "text-bnova-textPrimary" : "text-slate-800"
                }`}
              >
                Recent Activity
              </span>
              <div className="flex flex-col gap-4 mt-2">
                {mockActivity.map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <span
                      className={`text-[12px] font-mono whitespace-nowrap pt-0.5 ${
                        darkMode ? "text-bnova-textSecondary" : "text-slate-400"
                      }`}
                    >
                      {item.time}
                    </span>
                    <span
                      className={`text-[13px] ${
                        darkMode ? "text-bnova-textPrimary/95" : "text-slate-700"
                      }`}
                    >
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Grid>

          {/* AI Usage */}
          <Grid item xs={12} md={4}>
            <div
              className={`rounded-xl p-6 h-full border transition-colors shadow-sm ${
                darkMode
                  ? "bg-bnova-card border-bnova-border hover:border-white/10"
                  : "bg-white border-slate-200/60 hover:border-slate-300"
              }`}
            >
              <span
                className={`text-[14px] font-semibold mb-4 block ${
                  darkMode ? "text-bnova-textPrimary" : "text-slate-800"
                }`}
              >
                AI Usage
              </span>
              <div className="flex flex-col gap-4 mt-2">
                <div
                  className={`flex justify-between items-center pb-3 border-b ${
                    darkMode ? "border-bnova-border" : "border-slate-100"
                  }`}
                >
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textSecondary" : "text-slate-500"
                    }`}
                  >
                    GPT Requests
                  </span>
                  <span
                    className={`text-[14px] font-semibold ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-800"
                    }`}
                  >
                    12,431
                  </span>
                </div>
                <div
                  className={`flex justify-between items-center pb-3 border-b ${
                    darkMode ? "border-bnova-border" : "border-slate-100"
                  }`}
                >
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textSecondary" : "text-slate-500"
                    }`}
                  >
                    Embeddings
                  </span>
                  <span
                    className={`text-[14px] font-semibold ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-800"
                    }`}
                  >
                    45,322
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textSecondary" : "text-slate-500"
                    }`}
                  >
                    Tokens Used
                  </span>
                  <span
                    className={`text-[14px] font-semibold ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-800"
                    }`}
                  >
                    1.2M
                  </span>
                </div>
              </div>
            </div>
          </Grid>

          {/* System Health */}
          <Grid item xs={12} md={4}>
            <div
              className={`rounded-xl p-6 h-full border transition-colors shadow-sm ${
                darkMode
                  ? "bg-bnova-card border-bnova-border hover:border-white/10"
                  : "bg-white border-slate-200/60 hover:border-slate-300"
              }`}
            >
              <span
                className={`text-[14px] font-semibold mb-4 block ${
                  darkMode ? "text-bnova-textPrimary" : "text-slate-800"
                }`}
              >
                System Health
              </span>
              <div className="flex flex-col gap-4 mt-2">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-bnova-success shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-700"
                    }`}
                  >
                    PostgreSQL
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-bnova-success shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-700"
                    }`}
                  >
                    Redis
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-bnova-success shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-700"
                    }`}
                  >
                    Qdrant
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-bnova-success shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
                  <span
                    className={`text-[13px] ${
                      darkMode ? "text-bnova-textPrimary" : "text-slate-700"
                    }`}
                  >
                    AI Service
                  </span>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Dashboard;
